"use client";

import type { RefObject } from "react";
import { AudioWaveformVisualizer } from "../audio-waveform-visualizer";
import type { WaveformLevelTimeline } from "../waveform-level-timeline";
import { INTERACTIVE_AUDIO_SCRUB_HIT_HEIGHT_PX } from "./constants";
import { InteractiveAudioMultiSegmentWaveformTrack } from "./interactive-audio-multi-segment-waveform-track";
import type {
	InteractiveAudioSegmentRegion,
	InteractiveAudioSegmentVisualState,
} from "./types";

interface InteractiveAudioWaveformTrackProps {
	audioRef?: RefObject<HTMLAudioElement | null>;
	waveformTimeline?: WaveformLevelTimeline | null;
	isActive?: boolean;
	isPlaying?: boolean;
	isPreparingPlayback?: boolean;
	isLoading?: boolean;
	progressPercent: number;
	displayRevealPercent?: number;
	bufferRevealSessionKey?: number;
	bufferRevealSessionStartMs?: number;
	waveformSeed?: string;
	volumePercent?: number;
	scrubHitHeightPx?: number;
	maxSeekPercent?: number;
	furthestConsumedPercent?: number;
	continuationPercent?: number;
	scrubClampPercent?: number;
	segments?: InteractiveAudioSegmentRegion[];
	segmentStates?: Record<string, InteractiveAudioSegmentVisualState>;
	showParticles?: boolean;
	ambientParticleColor?: string;
	trackGlowColor1?: string;
	trackGlowColor2?: string;
	interactive?: boolean;
	ariaLabel?: string;
	onScrubPause?: () => void;
	onSeekCommit: (percent: number) => void;
	onSeekPreview?: (percent: number) => void;
	onSeekRejected?: () => void;
	className?: string;
	seekEpoch?: number;
}

export function InteractiveAudioWaveformTrack({
	audioRef,
	waveformTimeline = null,
	isActive = true,
	isPlaying = false,
	isPreparingPlayback = false,
	isLoading = false,
	progressPercent,
	displayRevealPercent = 0,
	bufferRevealSessionKey = 0,
	bufferRevealSessionStartMs = 0,
	waveformSeed = "audio-waveform",
	volumePercent,
	scrubHitHeightPx = INTERACTIVE_AUDIO_SCRUB_HIT_HEIGHT_PX,
	maxSeekPercent,
	furthestConsumedPercent,
	continuationPercent,
	scrubClampPercent,
	segments,
	segmentStates,
	showParticles,
	ambientParticleColor,
	trackGlowColor1,
	trackGlowColor2,
	interactive = true,
	ariaLabel,
	onScrubPause,
	onSeekCommit,
	onSeekPreview,
	onSeekRejected,
	className,
	seekEpoch = 0,
}: InteractiveAudioWaveformTrackProps) {
	if (segments != null && segments.length > 0) {
		return (
			<InteractiveAudioMultiSegmentWaveformTrack
				progressPercent={progressPercent}
				maxSeekPercent={maxSeekPercent}
				furthestConsumedPercent={furthestConsumedPercent}
				continuationPercent={continuationPercent}
				scrubClampPercent={scrubClampPercent}
				segments={segments}
				segmentStates={segmentStates}
				isActive={isActive}
				isPreparingPlayback={isPreparingPlayback}
				isLoading={isLoading}
				volumePercent={volumePercent}
				showParticles={showParticles}
				ambientParticleColor={ambientParticleColor}
				trackGlowColor1={trackGlowColor1}
				trackGlowColor2={trackGlowColor2}
				interactive={interactive}
				scrubHitHeightPx={scrubHitHeightPx}
				ariaLabel={ariaLabel}
				onScrubPause={onScrubPause}
				onSeekCommit={onSeekCommit}
				onSeekPreview={onSeekPreview}
				onSeekRejected={onSeekRejected}
				seekEpoch={seekEpoch}
				audioRef={audioRef}
				className={className}
			/>
		);
	}

	if (audioRef == null) {
		return null;
	}

	return (
		<AudioWaveformVisualizer
			audioRef={audioRef}
			waveformTimeline={waveformTimeline}
			isActive={isActive}
			isPlaying={isPlaying}
			isPreparingPlayback={isPreparingPlayback}
			isLoading={isLoading}
			progressPercent={progressPercent}
			displayRevealPercent={displayRevealPercent}
			bufferRevealSessionKey={bufferRevealSessionKey}
			bufferRevealSessionStartMs={bufferRevealSessionStartMs}
			waveformSeed={waveformSeed}
			volumePercent={volumePercent}
			scrubHitHeightPx={scrubHitHeightPx}
			onScrubPause={onScrubPause ?? (() => {})}
			onSeekCommit={onSeekCommit}
			seekEpoch={seekEpoch}
			className={className}
		/>
	);
}
